// Preserve native fullscreen where available; live MediaStreams on iPhone may
// reject both APIs, so retain an in-page expansion with accessible exit controls.
type VideoFullscreen = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};
export const MEDIA_EXPANDED = "media-expanded";
export const MEDIA_FULLSCREEN_CHANGE = "mediafullscreenchange";
const videoOf = (element: Element) => (element.matches("video") ? element : element.querySelector("video")) as VideoFullscreen | null;
export function mediaFullscreenActive(element: Element | null): boolean {
  return !!element && (element.ownerDocument.fullscreenElement === element || element.classList.contains(MEDIA_EXPANDED) || !!videoOf(element)?.webkitDisplayingFullscreen);
}
export function clearMediaExpansion(element: Element | null) {
  if (!element?.classList.contains(MEDIA_EXPANDED)) return;
  element.classList.remove(MEDIA_EXPANDED);
  element.ownerDocument.dispatchEvent(new Event(MEDIA_FULLSCREEN_CHANGE));
}
export async function toggleMediaFullscreen(element: Element | null): Promise<void> {
  if (!element) return;
  const doc = element.ownerDocument, video = videoOf(element);
  if (element.classList.contains(MEDIA_EXPANDED)) { clearMediaExpansion(element); return; }
  if (doc.fullscreenElement === element) { await doc.exitFullscreen(); return; }
  if (video?.webkitDisplayingFullscreen && video.webkitExitFullscreen) { video.webkitExitFullscreen(); return; }
  doc.querySelectorAll(`.${MEDIA_EXPANDED}`).forEach(clearMediaExpansion);
  if (typeof element.requestFullscreen === "function" && doc.fullscreenEnabled !== false) {
    try { await element.requestFullscreen(); return; } catch { /* Some mobile engines reject live media containers. */ }
  }
  if (video?.webkitEnterFullscreen) {
    try { video.webkitEnterFullscreen(); return; } catch { /* Live MediaStreams may not support the native video player. */ }
  }
  // At most one expanded tile; never clone the video or restart its tracks/audio.
  element.classList.add(MEDIA_EXPANDED);
  doc.dispatchEvent(new Event(MEDIA_FULLSCREEN_CHANGE));
}
